import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { getMagazineTopic, type MagazineTopic } from "@/data/magazine-topics";
import { type Photographer, type Theme } from "@/data/photographers";
import { fetchAllPhotographers } from "@/lib/photographers-source";
import { PortraitImage } from "@/components/PortraitImage";
import { transformedImageUrl, imageSrcSet } from "@/lib/image-url";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { detectInitialLocale } from "@/lib/i18n";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/magazin/$topic")({
  loader: ({ params }) => {
    const topic = getMagazineTopic(params.topic);
    if (!topic) throw notFound();
    return { topic };
  },
  head: ({ loaderData, params }) => {
    const topic = loaderData?.topic;
    if (!topic) return { meta: [{ title: "Magazin, OM Selected" }] };
    const locale = detectInitialLocale();
    const isEn = locale === "en";
    const base = "https://omselected.art/magazin";
    const deUrl = `${base}/${topic.slug}`;
    const enUrl = `${base}/${topic.slugEn}`;
    const url = isEn ? enUrl : deUrl;
    const title = isEn ? topic.titleEn : topic.title;
    const description = isEn ? topic.metaDescriptionEn : topic.metaDescription;
    const label = isEn ? topic.labelEn : topic.label;
    const faq = isEn ? topic.faqEn : topic.faq;
    return {
      meta: [
        { title: `${title}, OM Selected` },
        { name: "description", content: description },
        { property: "og:title", content: `${title}, OM Selected` },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "x-default", href: deUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: title,
            description,
            url,
            inLanguage: locale,
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: "https://omselected.art/" },
              { "@type": "ListItem", position: 2, name: "Magazin", item: "https://omselected.art/magazin/" },
              { "@type": "ListItem", position: 3, name: label, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Thema nicht gefunden</h1>
      <Link to="/magazin/$topic" params={{ topic: "portraitfotografie" }} className="eyebrow inline-block mt-8 border-b border-foreground pb-1">
        Zur Portraitfotografie
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
      <h1 className="serif text-4xl">Etwas ist schiefgelaufen</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: MagazinePage,
});

type Work = {
  id: string;
  image: string;
  caption: string;
  photographerId: string;
  photographerName: string;
  region?: string;
};

function MagazinePage() {
  const { topic } = Route.useLoaderData() as { topic: MagazineTopic };
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const [all, setAll] = useState<Photographer[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const label = isEn ? topic.labelEn : topic.label;
  const eyebrow = isEn ? topic.eyebrowEn : topic.eyebrow;
  const title = isEn ? topic.titleEn : topic.title;
  const lede = isEn ? topic.ledeEn : topic.lede;
  const intro = isEn ? topic.introEn : topic.intro;
  const sections = isEn ? topic.sectionsEn : topic.sections;
  const pullQuote = isEn ? topic.pullQuoteEn : topic.pullQuote;
  const checklist = isEn ? topic.checklistEn : topic.checklist;
  const faq = isEn ? topic.faqEn : topic.faq;
  const tt = isEn
    ? {
        home: "Home",
        sendRequest: "Send a request",
        viewGallery: "View gallery",
        selectedWorks: "Selected works",
        workBy: (name: string) => `${label}: work by ${name}`,
        checklistHeading: "What to look out for",
        photographersFor: (l: string) => `Photographers for ${l}`,
        viewAll: "View all",
        faqHeading: "Frequently asked questions",
        ctaHeading: "Ready for your project?",
        ctaBody:
          "Describe your project in a short briefing, and we'll connect you with matching, curated photographers.",
        creativeEyebrow: "For creatives",
        creativeHeading: "Are you a photographer or makeup artist?",
        creativeBody: (l: string) =>
          `Become part of OM Selected and receive matching enquiries for ${l}. Apply with your portfolio for the curated selection.`,
        applyNow: "Apply now",
      }
    : {
        home: "Start",
        sendRequest: "Anfrage senden",
        viewGallery: "Galerie ansehen",
        selectedWorks: "Ausgewählte Arbeiten",
        workBy: (name: string) => `${label}: Arbeit von ${name}`,
        checklistHeading: "Worauf du achten solltest",
        photographersFor: (l: string) => `Fotograf:innen für ${l}`,
        viewAll: "Alle ansehen",
        faqHeading: "Häufige Fragen",
        ctaHeading: "Bereit für dein Projekt?",
        ctaBody:
          "Beschreibe dein Vorhaben in einem kurzen Briefing, wir verbinden dich mit passenden, kuratierten Fotograf:innen.",
        creativeEyebrow: "Für Kreative",
        creativeHeading: "Du bist Fotograf:in oder Visagist:in?",
        creativeBody: (l: string) =>
          `Werde Teil von OM Selected und erhalte passende Anfragen für ${l}. Bewirb dich mit deinem Portfolio für die kuratierte Auswahl.`,
        applyNow: "Jetzt bewerben",
      };

  useEffect(() => {
    let active = true;
    fetchAllPhotographers().then((data) => {
      if (active) setAll(data);
    });
    return () => { active = false; };
  }, []);

  const matchingPhotographers = all
    .filter((p) => p.themes.includes(topic.theme as Theme))
    .slice(0, 8);

  const works: Work[] = useMemo(() => {
    const candidates = all.flatMap((p) =>
      (p.gallery ?? [])
        .map((src, i) => {
          const themesForSlot = (p.galleryThemesAll?.[i] ?? [p.galleryThemes?.[i] ?? "open"]) as Theme[];
          return {
            id: `${p.id}-${i}`,
            image: src,
            caption: p.name,
            photographerId: p.id,
            photographerName: p.name,
            region: p.city ?? p.region,
            _themes: themesForSlot,
          };
        })
        .filter((w) => w._themes.includes(topic.theme as Theme)),
    );
    // Shuffle (Fisher-Yates)
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }
    // Prefer one work per photographer
    const seen = new Set<string>();
    const unique: typeof candidates = [];
    for (const w of candidates) {
      if (seen.has(w.photographerId)) continue;
      seen.add(w.photographerId);
      unique.push(w);
    }
    const picked = unique.length >= 6 ? unique : [...unique, ...candidates.filter((w) => !unique.includes(w))];
    return picked.slice(0, 6);
  }, [all, topic.theme]);

  return (
    <>
    <div className="bg-background text-foreground">
      {/* Hero */}
      <section className="border-b border-border px-5 md:px-10">
        <div className="mx-auto max-w-[1280px] pt-12 md:pt-20 pb-16 md:pb-24">
          <nav aria-label="Brotkrumen" className="eyebrow text-muted-foreground mb-8">
            <Link to="/" className="hover:text-foreground">{tt.home}</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{label}</span>
          </nav>
          <span className="eyebrow text-muted-foreground block mb-6">{eyebrow}</span>
          <h1 className="serif text-4xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            {title}
          </h1>
          <p className="serif italic text-xl md:text-3xl mt-8 max-w-2xl leading-snug">
            {lede}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 eyebrow">
            <Link to="/brief" className="bg-foreground text-background px-8 py-4 hover:bg-foreground/85 transition-colors">
              {tt.sendRequest}
            </Link>
            <Link to="/themes/$theme" params={{ theme: topic.theme }} className="border border-foreground/20 px-8 py-4 hover:bg-muted transition-colors">
              {tt.viewGallery}
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground">{intro}</p>
        </div>
      </section>

      {/* Featured works */}
      {works.length > 0 && (
        <section className="px-5 md:px-10 border-b border-border bg-muted/40">
          <div className="mx-auto max-w-[1280px] py-16 md:py-24">
            <h2 className="serif text-3xl md:text-4xl mb-10">{tt.selectedWorks}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {works.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setLightboxIndex(works.indexOf(w))}
                  className="block group text-left w-full cursor-zoom-in"
                >
                  <div className="overflow-hidden bg-muted flex items-center justify-center">
                    <img
                      src={transformedImageUrl(w.image, { width: 700, quality: 76, resize: "contain" })}
                      srcSet={imageSrcSet(w.image, [400, 560, 700], { quality: 76, resize: "contain" })}
                      sizes="(min-width: 768px) 33vw, 50vw"
                      alt={tt.workBy(w.photographerName)}
                      loading="lazy"
                      className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                      onError={(e) => { const img = e.currentTarget; if (img.src !== w.image) { img.srcset = ""; img.src = w.image; } }}
                    />
                  </div>
                  <div className="mt-2 text-xs italic text-muted-foreground group-hover:text-foreground">{w.photographerName}</div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Editorial sections */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24 space-y-14">
          {sections.map((s) => (
            <article key={s.heading}>
              <h2 className="serif text-2xl md:text-3xl mb-4">{s.heading}</h2>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Pull quote */}
      <section className="px-5 md:px-10 border-b border-border bg-[oklch(0.16_0_0)] text-background">
        <div className="mx-auto max-w-[900px] py-20 md:py-28 text-center">
          <blockquote className="serif italic text-2xl md:text-4xl leading-tight">
            “{pullQuote}”
          </blockquote>
        </div>
      </section>

      {/* Checklist */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <h2 className="serif text-2xl md:text-3xl mb-8">{tt.checklistHeading}</h2>
          <ul className="space-y-4">
            {checklist.map((item, i) => (
              <li key={item} className="flex gap-4 items-baseline">
                <span className="serif text-muted-foreground text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-base md:text-lg leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Photographers */}
      {matchingPhotographers.length > 0 && (
        <section className="px-5 md:px-10 border-b border-border bg-muted/40">
          <div className="mx-auto max-w-[1280px] py-16 md:py-24">
            <div className="flex flex-wrap items-baseline justify-between gap-4 mb-10">
              <h2 className="serif text-3xl md:text-4xl">{tt.photographersFor(label)}</h2>
              <Link to="/themes/$theme" params={{ theme: topic.theme }} className="eyebrow border-b border-foreground pb-1 hover:opacity-60 transition-opacity">
                {tt.viewAll}
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-12">
              {matchingPhotographers.map((p) => (
                <Link key={p.id} to="/photographers/$id" params={{ id: p.id }} className="block">
                  <PortraitImage p={p} />
                  <div className="serif text-lg mt-4">{p.name}</div>
                  <div className="eyebrow text-muted-foreground mt-1">{p.region}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="px-5 md:px-10 border-b border-border">
        <div className="mx-auto max-w-[760px] py-16 md:py-24">
          <h2 className="serif text-3xl md:text-4xl mb-10">{tt.faqHeading}</h2>
          <div className="divide-y divide-border border-t border-border">
            {faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="serif text-lg md:text-xl cursor-pointer list-none flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-muted-foreground transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[900px] py-20 md:py-28 text-center">
          <h2 className="serif text-3xl md:text-5xl mb-6">{tt.ctaHeading}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed">
            {tt.ctaBody}
          </p>
          <Link to="/brief" className="eyebrow inline-block bg-foreground text-background px-10 py-4 hover:bg-foreground/85 transition-colors">
            {tt.sendRequest}
          </Link>
        </div>
      </section>

      {/* CTA für Kreative */}
      <section className="px-5 md:px-10 border-t border-border bg-muted/40">
        <div className="mx-auto max-w-[900px] py-20 md:py-28 text-center">
          <span className="eyebrow text-muted-foreground block mb-6">{tt.creativeEyebrow}</span>
          <h2 className="serif text-3xl md:text-5xl mb-6">{tt.creativeHeading}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed">
            {tt.creativeBody(label)}
          </p>
          <Link to="/apply" className="eyebrow inline-block border border-foreground px-10 py-4 hover:bg-foreground hover:text-background transition-colors">
            {tt.applyNow}
          </Link>
        </div>
      </section>
    </div>
    <PhotoLightbox
      info={lightboxIndex !== null && works[lightboxIndex] ? { image: works[lightboxIndex].image, caption: works[lightboxIndex].caption, photographerName: works[lightboxIndex].photographerName, photographerId: works[lightboxIndex].photographerId, region: works[lightboxIndex].region } : null}
      onClose={() => setLightboxIndex(null)}
      onPrev={lightboxIndex !== null && lightboxIndex > 0 ? () => setLightboxIndex((i) => (i ?? 0) - 1) : undefined}
      onNext={lightboxIndex !== null && lightboxIndex < works.length - 1 ? () => setLightboxIndex((i) => (i ?? 0) + 1) : undefined}
      neighborImages={lightboxIndex !== null ? [works[lightboxIndex - 1]?.image, works[lightboxIndex + 1]?.image] : undefined}
    />
    </>
  );
}